import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
    enrichStationWithThemeBackgrounds,
    readActiveStoredStation,
    resetHomeAppState,
    type StoredStationRecord,
} from "../../../utils/stationFlowStorage";
import { AppDispatch } from "../../../redux/store";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
import type { AiSelfieArt } from "../../../services/aiSelfieArtService";
import { readSelectedAiSelfieArtFromStorage } from "../../../services/stylePromptResolver";
import { readPendingOrderNumbers } from "../../../utils/pendingPaymentOrder";

/** Time the order number(s) stay on screen before returning home. */
const REDIRECT_DELAY_MS = 10000;

type OrderConfirmationState = {
    stationData?: StoredStationRecord;
    selectedArt?: Partial<AiSelfieArt> | null;
    orderNumbers?: Array<string | number>;
    orderNumber?: string | number;
};

function resolveCategoryName(
    selectedArt?: Partial<AiSelfieArt> | null,
    station?: StoredStationRecord | null
): string {
    const art = selectedArt || readSelectedAiSelfieArtFromStorage();
    // Prefer the art chosen on /all (e.g. "Wynwood Caricature"), not parent category.
    return (
        art?.name?.trim() ||
        art?.category?.name?.trim() ||
        station?.name?.trim() ||
        "order"
    );
}

const OrderConfirmation = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const { stationData, selectedArt } = (location.state || {}) as OrderConfirmationState;

    const themeStation = useMemo(
        () => enrichStationWithThemeBackgrounds(stationData || readActiveStoredStation(), selectedArt),
        [selectedArt, stationData]
    );

    const categoryName = useMemo(
        () => resolveCategoryName(selectedArt, themeStation),
        [selectedArt, themeStation]
    );

    const orderNumbers = useMemo(() => {
        const state = (location.state || {}) as OrderConfirmationState;
        if (Array.isArray(state.orderNumbers) && state.orderNumbers.length > 0) {
            return state.orderNumbers.map((value) => String(value).trim()).filter(Boolean);
        }
        if (state.orderNumber != null && String(state.orderNumber).trim()) {
            return [String(state.orderNumber).trim()];
        }
        return readPendingOrderNumbers();
    }, [location.state]);

    useEffect(() => {
        const timer = setTimeout(() => {
            resetHomeAppState(dispatch);
            navigate("/", { replace: true });
        }, REDIRECT_DELAY_MS);

        return () => clearTimeout(timer);
    }, [dispatch, navigate]);

    const hasMultipleOrders = orderNumbers.length > 1;

    return (
        <StationThemeLayout
            className="successPaymentSection paymentSuccessThemePage"
            station={themeStation}
            showStationLogo
            showBack={false}
        >
            <div className="h-100">
                <div className="qrCodePaySection h-100">
                    <div className="qrCard text-center">
                        <div className="heading">
                            {hasMultipleOrders ? (
                                <>Your orders have been <br /> placed successfully</>
                            ) : (
                                <>Your order has been <br /> placed successfully</>
                            )}
                        </div>
                        {orderNumbers.length > 0 && (
                            <div className="orderDetails">
                                {orderNumbers.map((orderNumber) => (
                                    <div className="orderNumber" key={orderNumber}>
                                        ORDER #{orderNumber}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="description mt-4">
                            Please claim your <strong>{categoryName}</strong> <br /> at{" "}
                            <strong>MOSIDA Print Center</strong>
                        </div>
                    </div>
                </div>
            </div>
        </StationThemeLayout>
    );
};

export default OrderConfirmation;
